/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/
// React
import React from 'react';
import { Controller } from 'react-hook-form';
// material-ui components
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
// custom components
import NumberedHeading from '../../../../../shared-components/forms/NumberedHeading';
import SingleReadingMCQuestion from './components/SingleReadingMCQuestion';

const AssessmentReadingMultipleChoice = (props) => {
  const { control, formState } = props.methods;
  const { errors } = formState;
  const { inputDir, possiblePages } = props;

  return (
    <div>
      <NumberedHeading
        number="1"
        title="Reading Title (TL)"
        instructions="Please give a title to the reading in the Target Language."
      />
      <Controller
        control={control}
        name="content.reading_title_tl"
        render={({ field }) => (
          <TextField
            className="mt-16"
            {...field}
            label="Title (TL)"
            placeholder="Title (TL)"
            id="video"
            error={!!errors.content?.reading_title_tl}
            helperText={errors.content?.reading_title_tl?.message}
            variant="outlined"
            fullWidth
            required
            dir={inputDir}
          />
        )}
      />

      <Divider className="my-16" />

      <NumberedHeading
        number="2"
        title="Reading Title (EN)"
        instructions="Please give a title to the reading in English."
      />
      <Controller
        control={control}
        name="content.reading_title_en"
        render={({ field }) => (
          <TextField
            className="mt-16"
            {...field}
            label="Title (EN)"
            placeholder="Title (EN)"
            id="video"
            error={!!errors.content?.reading_title_en}
            helperText={errors.content?.reading_title_en?.message}
            variant="outlined"
            fullWidth
            required
          />
        )}
      />

      <Divider className="my-16" />

      <NumberedHeading
        number="3"
        title="Source URL"
        instructions="Please provide the link to the reading."
      />
      <Controller
        control={control}
        name="content.source_url"
        render={({ field }) => (
          <TextField
            className="mt-16"
            {...field}
            label="Source URL"
            placeholder="Source URL"
            id="video"
            error={!!errors.content?.source_url}
            helperText={errors.content?.source_url?.message}
            variant="outlined"
            fullWidth
            required
          />
        )}
      />

      <Divider className="my-16" />

      <NumberedHeading
        number="4"
        title="Media URL"
        instructions="Please provide the link to the material on Sharepoint"
      />
      <Controller
        control={control}
        name="content.media_url"
        render={({ field }) => (
          <TextField
            className="mt-16"
            {...field}
            label="Media URL"
            placeholder="Media URL"
            id="video"
            error={!!errors.content?.media_url}
            helperText={errors.content?.media_url?.message}
            variant="outlined"
            fullWidth
          />
        )}
      />

      <Divider className="my-16" />

      <NumberedHeading
        number="5"
        title="Material Location"
        instructions="Select where this material is from"
      />
      <Controller
        control={control}
        name="content.material_location"
        render={({ field }) => (
          <FormControl
            fullWidth
            className="mt-16"
            error={!!errors.content?.material_location}
          >
            <InputLabel id="select-label">Material Location</InputLabel>
            <Select
              {...field}
              labelId="select-material-location"
              id="material_location"
              label="Material Location"
              classes={{ select: 'flex items-center space-x-12' }}
              defaultValue="none"
            >
              <MenuItem value="none" disabled className="space-x-12">
                <span>Select a location</span>
              </MenuItem>
              <MenuItem value="elo_1" className="space-x-12">
                ELO 1
              </MenuItem>
              <MenuItem value="elo_1_pe" className="space-x-12">
                ELO 1 PE
              </MenuItem>
              <MenuItem value="elo_2" className="space-x-12">
                ELO 2
              </MenuItem>
              <MenuItem value="elo_2_pe" className="space-x-12">
                ELO 2 PE
              </MenuItem>
              <MenuItem value="fpe" className="space-x-12">
                FPE
              </MenuItem>
            </Select>
            {!!errors.content?.material_location ? (
              <FormHelperText>
                {errors?.content?.material_location?.message}
              </FormHelperText>
            ) : null}
          </FormControl>
        )}
      />

      <Divider className="my-16" />

      <NumberedHeading
        number="6"
        title="Material Reference ID"
        instructions="Please provide the page id for material. Leave this for the devs."
      />
      <Controller
        control={control}
        name="content.media_page_id"
        render={({ field }) => (
          <>
            <FormControl
              className="mt-16"
              fullWidth
              size="small"
              error={!!errors.content?.media_page_id}
            >
              <InputLabel id="material-page-select-label">
                Material Reference Id
              </InputLabel>
              <Select
                labelId="material-page-select-label"
                id="material-page-select"
                {...field}
                label="Material Page"
                value={
                  possiblePages.find((page) => page.id === field.value)
                    ? field.value
                    : ''
                }
              >
                <MenuItem value="" disabled>
                  <em>Material Reference Id</em>
                </MenuItem>
                {possiblePages.map((page) => (
                  <MenuItem key={page.id} value={page.id}>
                    {page.label} - {page.title}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {errors.content?.media_page_id?.message}
              </FormHelperText>
            </FormControl>
          </>
        )}
      />

      <Divider className="my-16" />

      <NumberedHeading
        number="7"
        title="Question"
        instructions="Fill out the form inputs below to add a question for the activity."
      />
      <Controller
        control={control}
        name="content.question"
        render={({ field }) => (
          <SingleReadingMCQuestion
            inputDir={inputDir}
            control={control}
            errors={errors}
          />
        )}
      />
    </div>
  );
};

export default AssessmentReadingMultipleChoice;
